import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../app/store";
import { LatLngTuple } from "leaflet";
import { BillType } from "../bill/billSlice";

export interface CompaniesTypeState {
  companies: Array<CompanyType>;
}

export interface CompanyType {
  _id?: string;
  user: string;
  shortName: string;
  name: string;
  website: string;
  email: string;
  phone: string;
  NIP: string;
  description: string;
  mainField: Array<string>;
  coordinates: LatLngTuple;
  city: string;
  street: string;
  streetNumber: string;
  localNumber?: string | null;
  zipCode: string;
  voivodeship: string;
  isInternational?: boolean;
  isADR?: boolean;
  isOversized?: boolean;
  addDate: string;
  expirationDate: string | null;
  period: number;
  highlightedExpirationDate: string | null;
  recommendedExpirationDate: string | null;
  newIsHighlighted: boolean;
  newIsRecommended: boolean;
  isActive?: boolean;
  addHighlighted?: boolean;
  addNewPeriod?: boolean;
  addRecommended?: boolean;
  newPeriod: number;
  newExpirationDate: string;
  price: number;
  additionalDescription?: string;
  images?: {
    logo?: string | null;
    photos?: string[];
  };
}

export interface EditCompany {
  _id?: string;
  user: string;
  shortName: string;
  name: string;
  website: string;
  email: string;
  phone: string;
  NIP: string;
  description: string;
  additionalDescription?: string;
  isInternational?: boolean;
  isADR?: boolean;
  isOversized?: boolean;
}

export interface addNewPeriodAndHilight {
  _id?: string;
  newExpirationDate: string;
  newPeriod: number;
  newIsHighlighted: boolean;
  price: number;
  user: string;
}

export interface updateCoordinatesAndIsActive {
  _id?: string;
  coordinates: LatLngTuple;
  user: string;
  price: number;
  isActive?: boolean;
}

export interface updateResult {
  success: string;
}

export interface idOffer {
  _id?: string;
  user?: string;
}

export interface idAndPrice {
  _id?: string;
  user?: string;
  price?: number;
}


export interface NewPeriodAdminObjectType {
  _id?: string;
  newExpirationDate: string;
  newPeriod: number;
  newIsHighlighted: boolean;
  price: number;
  user: string;
}

export interface OfferState {
  companies: CompanyType[];
  selectedCompany: CompanyType | null;
  isLoading: boolean;
  error: string;
  msg: string;
}

export interface AddNewPeriodPayload {
  _id: string;
  newPeriod: number;
  newIsHighlighted: boolean;
  newIsRecommended: boolean;
}

export interface AddPromotionPayload {
  _id: string;
  promotionType: "highlighted" | "recommended";
}

export interface ActivateCompanyPayload {
  id: string;
  coordinates?: [number, number];
}

export interface ActivateCompanyResponse {
  success: boolean;
  message: string;
  offer: CompanyType;
}

const initialState: OfferState = {
  companies: [],
  isLoading: false,
  selectedCompany: null,
  error: "",
  msg: "",
};

interface AddCompanyResponse {
  company: CompanyType;  // oferta, która została dodana
  bill?: BillType | null;
}

export type AddCompanyPayload = Omit<
  CompanyType,
  "highlightedExpirationDate" | "recommendedExpirationDate"
> & {
  isHighlighted: boolean;
  isRecommended: boolean;
};

export const addNewCompany = createAsyncThunk<
  AddCompanyResponse,
  AddCompanyPayload,
  { rejectValue: { msg: string } }
>(
  "offer/addNewOffer",
  async (offerObject, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/offers/new", offerObject, {
        headers: {
          "x-auth-token": localStorage.getItem("token") || "",
        },
      });

      // payload zawiera company
      return {
        company: response.data.offer,
      };
    } catch (err) {
        const error = err as any
        return rejectWithValue({
          msg: error.response?.data?.error || "Błąd serwera",
      });
    }
  }
);

export const addNewCompanyAsAdmin = createAsyncThunk<
  AddCompanyResponse,
  AddCompanyPayload,
  { rejectValue: { msg: string } }
>(
  "offer/addNewOfferAsAdmin",
  async (offerObject, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/offers/admin/new", offerObject, {
        headers: {
          "x-auth-token": localStorage.getItem("token") || "",
        },
      });

      // payload zawiera company
      return {
        company: response.data.offer,
      };
    } catch (err) {
        const error = err as any
        return rejectWithValue({
          msg: error.response?.data?.error || "Błąd serwera",
      });
    }
  }
);

export const addNewPeriodUser = createAsyncThunk(
  "companies/addNewPeriodUser",
  async (
    {
      _id,
      newPeriod,
      newIsHighlighted,
      newIsRecommended,
    }: AddNewPeriodPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        "/api/offers/add-new-period-user",
        {
          _id,
          newPeriod,
          newIsHighlighted,
          newIsRecommended,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token":
              localStorage.getItem("token") || "",
          },
        }
      );

      return response.data;
    } catch (err) {
      const error = err as any;

      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.msg ||
          "Nie udało się zamówić przedłużenia"
      );
    }
  }
);

export const editCompany = createAsyncThunk(
  "offer/editCompany",
  // Declare the type your function argument here:

  async (editObject: EditCompany) => {
    
    const response = await axios.post("/api/offers/edit", editObject, {
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": `${localStorage.getItem("token")}`,
      },
    });
    
    return response.data as CompanyType;
  }
);

export const addNewPeriodAdmin = createAsyncThunk<
  unknown,
  NewPeriodAdminObjectType,
  { rejectValue: { msg: string } }
>(
  "offer/addNewPeriodAdmin",
  // Declare the type your function argument here:

  async (addNewPeriodAdminObject, { rejectWithValue }) => {
    try { 
    const response = await axios.post("/api/offers/add-new-period-admin", addNewPeriodAdminObject, {
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": `${localStorage.getItem("token")}`,
      },
    });

    return response.data;
  } catch (err) {
      const error = err as any
    return rejectWithValue({
      msg: error.response?.data?.error || "Błąd serwera",
    });
  }
});


export const addPromotionUser = createAsyncThunk(
  "companies/addPromotionUser",
  async (
    {
      _id,
      promotionType,
    }: AddPromotionPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        "/api/offers/promotion",
        {
          _id,
          promotionType,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token":
              localStorage.getItem("token") || "",
          },
        }
      );

      return response.data;
    } catch (err) {
      const error = err as any;

      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.msg ||
          "Nie udało się zamówić promocji"
      );
    }
  }
);
export const addNewHighlightedAdmin = createAsyncThunk<
  unknown,
  idAndPrice,
  { rejectValue: { msg: string } }
>
(
  "offer/addNewHighlightedAdmin",
  // Declare the type your function argument here:

  async (offerIdAndPrice, { rejectWithValue }) => {
    try {
    const response = await axios.post("/api/offers/to-highlight-admin", offerIdAndPrice, {
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": `${localStorage.getItem("token")}`,
      },
    });

    return response.data;
  } catch (err) {
      const error = err as any
    return rejectWithValue({
      msg: error.response?.data?.error || "Błąd serwera",
    });
  }
});

export const activateCompany = createAsyncThunk<
  ActivateCompanyResponse,
  ActivateCompanyPayload,
  { rejectValue: string }
>(
  "companies/activateCompany",
  async (
    { id, coordinates },
    { rejectWithValue }
  ) => {
    try {
      const response =
        await axios.patch<ActivateCompanyResponse>(
          `/api/offers/admin/${id}/activate`,
          coordinates
            ? {
                coordinates,
              }
            : {},
          {
            headers: {
              "Content-Type": "application/json",
              "x-auth-token":
                localStorage.getItem("token") || "",
            },
          }
        );

      return response.data;
    } catch (err) {
      const error = err as any;

      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.msg ||
          "Nie udało się aktywować oferty"
      );
    }
  }
);

export const activeCompany = createAsyncThunk(
  "offer/activeAndInsertNewCoordinates",
  // Declare the type your function argument here:

  async (offerCoordinates: updateCoordinatesAndIsActive) => {

    const response = await axios.post("/api/offers/update", offerCoordinates, {
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": `${localStorage.getItem("token")}`,
      },
    });

    return (await response.data) as CompanyType;
  }
);

export const fetchAllCompanies = createAsyncThunk(
  "offers/fetchAllCompanies",
  // Declare the type your function argument here:
  async () => {
    const response = await axios.get("/api/offers/");
    return (await response.data) as CompanyType[];
  }
);


export const fetchCompaniesDisabled = createAsyncThunk(
  "offers/fetchDisabled",
  // Declare the type your function argument here:
  async () => {
    let url: string = "/api/offers/disabled/";
    const response = await axios.get(url, { headers: {
        "Content-Type": "application/json",
        "x-auth-token": `${localStorage.getItem("token")}`,
      } });
    return (await response.data) as CompanyType[];
  }
);
export const fetchCompaniesToHighlight = createAsyncThunk(
  "offers/fetchToHighlight",
  // Declare the type your function argument here:

  async () => {
    const headers = {
      "Content-Type": "application/json",
      "x-auth-token": `${localStorage.getItem("token")}`,
    };
    let url: string = "/api/offers/to-highlight-find/";
    const response = await axios.get(url, { headers: headers });
    return (await response.data) as CompanyType[];
  }
);

export const fetchCompaniesToAddPeriod = createAsyncThunk(
  "offers/fetchAddNewPeriod",
  // Declare the type your function argument here:

  async () => {
    const headers = {
      "Content-Type": "application/json",
      "x-auth-token": `${localStorage.getItem("token")}`,
    };
    let url: string = "/api/offers/add-new-period-find/";
    const response = await axios.get(url, { headers: headers });
    return (await response.data) as CompanyType[];
  }
);

export const fetchCompaniesExpired = createAsyncThunk(
  "offers/fetchExpired",
  // Declare the type your function argument here:

  async () => {
    const headers = {
      "Content-Type": "application/json",
      "x-auth-token": `${localStorage.getItem("token")}`,
    };
    let url: string = "/api/offers/expired/";
    const response = await axios.get(url, { headers: headers });
    return (await response.data) as CompanyType[];
  }
);

export const fetchCompaniesCloseToExpire = createAsyncThunk(
  "offers/closeToExpire",
  // Declare the type your function argument here:

  async () => {
    const headers = {
      "Content-Type": "application/json",
      "x-auth-token": `${localStorage.getItem("token")}`,
    };
    let url: string = "/api/offers/close-to-expire/";
    const response = await axios.get(url, { headers: headers });
    return (await response.data) as CompanyType[];
  }
);

export const fetchCompaniesFiltered = createAsyncThunk(
  "offers/fetchByFilter",
  // Declare the type your function argument here:
  async (myArray: Array<string | boolean>) => {
    let url: string =
      "/api/offers/sorted/" +
      myArray[0] +
      "/" +
      myArray[1] +
      "/" +
      myArray[2] +
      "/" +
      myArray[3] +
      "/" +
      myArray[4];
    const response = await axios.get(url);
    return (await response.data) as CompanyType[];
  }
);

export const fetchUserCompanies = createAsyncThunk<
  CompanyType[],
  void
>("offers/fetchByUser", async () => {
  const token = localStorage.getItem("token");

  const response = await axios.get("/api/offers/my-offers", {
    headers: {
      "x-auth-token": token,
    },
  });

  return response.data;
});


export const findById = createAsyncThunk(
  "offers/findById",
  async (id: string) => {
    const response = await axios.get("/api/offers/" + id);
    return (await response.data) as CompanyType;
  }
);

export const companySlice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    clearCompanies: (state) => {
      state.companies = [];
    },
    updateCompanyImages: (
      state,
      action: PayloadAction<{
        id: string;
        images: NonNullable<CompanyType["images"]>;
      }>
    ) => {
      const company = state.companies.find(
        (item) => item._id === action.payload.id
      );

      if (company) {
        company.images = action.payload.images;
      }

      if (state.selectedCompany?._id === action.payload.id) {
        state.selectedCompany.images = action.payload.images;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchAllCompanies.fulfilled,
      (state, action: PayloadAction<CompanyType[]>) => {
        state.isLoading = false;
        state.companies = action.payload;
      }
    )
    .addCase(addNewCompany.pending, (state) => {
      state.isLoading = true;
      state.msg = "";
    })
    .addCase(addNewCompany.fulfilled, (state, action: PayloadAction<AddCompanyResponse>) => {
      // dodanie nowej oferty
      //state.companies.push(action.payload.company);

      // aktualizacja balance w store i localStorage
      state.isLoading = false;
    })
    .addCase(addNewCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.msg = action.payload?.msg || "Błąd dodawania oferty";
      })
      .addCase(addNewCompanyAsAdmin.pending, (state) => {
      state.isLoading = true;
      state.msg = "";
    })
    .addCase(addNewCompanyAsAdmin.fulfilled, (state, action: PayloadAction<AddCompanyResponse>) => {
      // dodanie nowej oferty
      //state.companies.push(action.payload.company);

      // aktualizacja balance w store i localStorage
      state.isLoading = false;
    })
    .addCase(addNewCompanyAsAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.msg = action.payload?.msg || "Błąd dodawania oferty";
      })
      .addCase(fetchCompaniesFiltered.pending, (state) => {
        state.isLoading = true;
        state.error = "";
      })
    .addCase(
      fetchCompaniesFiltered.fulfilled,
      (state, action: PayloadAction<CompanyType[]>) => {
        state.isLoading = false;
        state.companies = action.payload;
      }
    )
    .addCase(fetchCompaniesFiltered.rejected, (state) => {
      state.isLoading = false;
      state.error = "Nie udało się pobrać ofert";
    })
    .addCase(
      fetchCompaniesDisabled.fulfilled,
      (state, action: PayloadAction<CompanyType[]>) => {
        state.companies = action.payload;
      }
    )
    .addCase(
      fetchCompaniesToHighlight.fulfilled,
      (state, action: PayloadAction<CompanyType[]>) => {
        state.companies = action.payload;
      }
    )
    .addCase(
      fetchCompaniesToAddPeriod.fulfilled,
      (state, action: PayloadAction<CompanyType[]>) => {
        state.companies = action.payload;
      }
    )
    .addCase(
      activeCompany.fulfilled,
      (state, action: PayloadAction<CompanyType>) => {
      }
    )
    .addCase(addNewHighlightedAdmin.fulfilled, () => {})
    .addCase(addNewPeriodUser.pending, (state) => {
      state.isLoading = true;
      state.error = "";
    })
    .addCase(addNewPeriodUser.fulfilled, (state, action) => {
      state.isLoading = false;

      const updatedOffer = action.payload.offer;

      const index = state.companies.findIndex(
        (company) => company._id === updatedOffer._id
      );

      if (index !== -1) {
        state.companies[index] = updatedOffer;
      }

      state.msg = action.payload.message || "";
    })
    .addCase(addNewPeriodUser.rejected, (state, action) => {
      state.isLoading = false;
      state.error =
        (action.payload as string) ||
        "Nie udało się zamówić przedłużenia";
    })
    .addCase(addNewPeriodAdmin.fulfilled, () => {})
    .addCase(
      fetchCompaniesExpired.fulfilled,
      (state, action: PayloadAction<CompanyType[]>) => {
        state.companies = action.payload;
      }
    )
    .addCase(
      fetchCompaniesCloseToExpire.fulfilled,
      (state, action: PayloadAction<CompanyType[]>) => {
        state.companies = action.payload;
      }
    )
    .addCase(fetchUserCompanies.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(
      fetchUserCompanies.fulfilled,
      (state, action: PayloadAction<CompanyType[]>) => {
        state.isLoading = false;          
        state.companies = action.payload; 
      }
    )
    .addCase(fetchUserCompanies.rejected, (state) => {
      state.isLoading = false;           
    })
    .addCase(findById.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(
      findById.fulfilled,
      (state, action: PayloadAction<CompanyType>) => {
        state.isLoading = false;
        state.selectedCompany = action.payload;
      }
    )
    .addCase(findById.rejected, (state) => {
      state.isLoading = false;
    })
    .addCase(addPromotionUser.pending, (state) => {
      state.isLoading = true;
      state.error = "";
    })
    .addCase(addPromotionUser.fulfilled, (state, action) => {
      state.isLoading = false;

      const updatedOffer = action.payload.offer;

      const index = state.companies.findIndex(
        (company) => company._id === updatedOffer._id
      );

      if (index !== -1) {
        state.companies[index] = updatedOffer;
      }
    })
    .addCase(addPromotionUser.rejected, (state, action) => {
      state.isLoading = false;
      state.error =
        (action.payload as string) ||
        "Nie udało się zamówić promocji";
    })
    .addCase(activateCompany.pending, (state) => {
      state.isLoading = true;
      state.error = "";
      state.msg = "";
    })
    .addCase(activateCompany.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = "";
      state.msg =
        action.payload.message ||
        "Oferta została aktywowana";

      /*
      * Usuwamy aktywowaną ofertę z listy nieaktywnych.
      */
      state.companies = state.companies.filter(
        (company) =>
          company._id !== action.payload.offer._id
      );
    })
    .addCase(activateCompany.rejected, (state, action) => {
      state.isLoading = false;
      state.msg = "";
      state.error =
        action.payload ||
        "Nie udało się aktywować oferty";
    });
  },
});
export const {
  clearCompanies,
  updateCompanyImages,
} = companySlice.actions;
export const selectCompanies = (state: RootState) => state.companies.companies;
export const selectCompany = (state: RootState) => state.companies.selectedCompany;
export default companySlice.reducer;